import React, { useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { PAYMENTS_BY_USER } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import './Page.css';
import './Payments.css';

function formatUnixTs(ts) {
  if (ts == null || ts === '') return '—';
  const n = Number(ts);
  if (Number.isNaN(n)) return '—';
  return new Date(n * 1000).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatIsoDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function isLikelyPhone(value) {
  const cleaned = (value || '').replace(/\s/g, '');
  return /^[\d+\-()]+$/.test(cleaned) && cleaned.length >= 10;
}

function Payments() {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [record, setRecord] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    const value = (input || '').trim();
    if (!value) {
      setError('Enter a user ID or phone number.');
      setRecord(null);
      return;
    }
    setError('');
    setRecord(null);
    setLoading(true);
    const params = isLikelyPhone(value) ? { phone_number: value } : { user_id: value };
    const path = PAYMENTS_BY_USER.pathWithQuery(params);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(apiUrl(path), { method: PAYMENTS_BY_USER.method, headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status));
        setLoading(false);
        return;
      }
      const payload = data.data || data;
      setRecord(payload);
    } catch (err) {
      setError(err.message || 'Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page payments-page">
      <div className="payments-header">
        <h3>View / Manage Payments</h3>
        <p className="payments-subtitle">Search historic payments by user ID or phone number.</p>
      </div>

      <form onSubmit={handleSearch} className="payments-search-card">
        <div className="payments-search-row">
          <label htmlFor="payments-search-input" className="payments-search-label">
            User ID or phone number <span className="label-required" aria-hidden="true">*</span>
          </label>
          <div className="payments-search-input-wrap">
            <input
              id="payments-search-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. 796be5d4-4832-497a-be13-a3bd5a044cbb or +91-7894332058"
              disabled={loading}
              autoComplete="off"
              className="payments-search-input"
            />
            <button type="submit" className="payments-search-btn" disabled={loading}>
              <FiSearch className="payments-search-btn-icon" aria-hidden />
              {loading ? 'Searching…' : 'Search historic payments'}
            </button>
          </div>
        </div>
        {error && <p className="payments-error" role="alert">{error}</p>}
      </form>

      {record && (
        <div className="payments-result-card">
          <h4 className="payments-result-title">Payment record</h4>
          <div className="payments-result-grid">
            <div className="payments-result-field">
              <span className="payments-result-label">User ID</span>
              <span className="payments-result-value payments-result-value-id">{record.user_id || '—'}</span>
            </div>
            <div className="payments-result-field">
              <span className="payments-result-label">Subscription status</span>
              <span className={`payments-status-pill payments-status-${(record.subscription_status || '').toLowerCase()}`}>
                {record.subscription_status || '—'}
              </span>
            </div>
            <div className="payments-result-field">
              <span className="payments-result-label">Payment recurrence count</span>
              <span className="payments-result-value">{record.payment_recurrence_count ?? '—'}</span>
            </div>
            <div className="payments-result-field">
              <span className="payments-result-label">First subscription payment</span>
              <span className="payments-result-value">{formatUnixTs(record.first_subscription_payment_captured_at)}</span>
            </div>
            <div className="payments-result-field">
              <span className="payments-result-label">Latest subscription payment</span>
              <span className="payments-result-value">{formatUnixTs(record.latest_subscription_payment_captured_at)}</span>
            </div>
            <div className="payments-result-field">
              <span className="payments-result-label">Subscription end date</span>
              <span className="payments-result-value">{formatUnixTs(record.subscription_end_date)}</span>
            </div>
            <div className="payments-result-field">
              <span className="payments-result-label">Trial payment captured</span>
              <span className="payments-result-value">{formatUnixTs(record.trial_payment_captured_at)}</span>
            </div>
            <div className="payments-result-field">
              <span className="payments-result-label">Trial valid until</span>
              <span className="payments-result-value">{formatUnixTs(record.trial_valid_until)}</span>
            </div>
            <div className="payments-result-field payments-result-field-full">
              <span className="payments-result-label">Last updated</span>
              <span className="payments-result-value">{formatIsoDate(record.updated_at)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payments;
